import React, { useMemo, useState } from 'react';
import { Image, Modal, TouchableOpacity, View } from 'react-native';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import { Button, Icon, Text } from '..';
import { TextInputEditInPlace } from '../input';
import ImageViewer from './ImageViewer';

const normalizePhotos = (photos = [], photoUrls = []) => {
	const seen = new Set();
	const normalizedPhotos = [];

	(photos || []).forEach(photo => {
		if (!photo?.link || seen.has(photo.link)) {
			return;
		}

		seen.add(photo.link);
		normalizedPhotos.push(photo);
	});

	(photoUrls || []).forEach(photoUrl => {
		if (typeof photoUrl !== 'string' || !photoUrl.trim() || seen.has(photoUrl)) {
			return;
		}

		seen.add(photoUrl);
		normalizedPhotos.push({
			id: photoUrl,
			link: photoUrl,
			legacyUrl: true,
		});
	});

	return normalizedPhotos;
};

const VisualDirectionPhotoGrid = ({
	addButtonLabel = 'Add visual direction photo',
	canEdit = false,
	emptyText = 'No visual direction photos.',
	onAddPhoto,
	onRemovePhoto,
	onUpdatePhotoComment,
	photos,
	photoUrls,
	style,
}) => {
	const styles = useThemedStyles(createStyles);
	const [currentPhotoIndex, setCurrentPhotoIndex] = useState(null);
	const normalizedPhotos = useMemo(() => normalizePhotos(photos, photoUrls), [photos, photoUrls]);
	const imageViewerUrls = useMemo(() => normalizedPhotos.map(photo => ({ url: photo.link })), [normalizedPhotos]);

	return (
		<View style={style}>
			{canEdit && onAddPhoto && (
				<Button
					label={addButtonLabel}
					onPress={onAddPhoto}
					style={styles.addButton}
				/>
			)}
			{normalizedPhotos.length > 0 ? (
				<View style={styles.photoGrid}>
					{normalizedPhotos.map((photo, index) => (
						<View key={photo.id || photo.link} style={styles.photoCard}>
							<TouchableOpacity
								onPress={() => setCurrentPhotoIndex(index)}
								style={styles.photoTile}
							>
								<Image
									source={{ uri: photo.link }}
									style={styles.photo}
								/>
								{canEdit && onRemovePhoto && (
									<TouchableOpacity
										onPress={() => onRemovePhoto(photo, index)}
										style={styles.removeButton}
									>
										<Icon name="close" size={12} color={styles.removeIcon.color} />
									</TouchableOpacity>
								)}
							</TouchableOpacity>
							{canEdit && onUpdatePhotoComment && !photo.legacyUrl ? (
								<TextInputEditInPlace
									value={photo.comment}
									field="comment"
									placeholder="Add a description"
									onConfirm={(payload) => onUpdatePhotoComment(photo, payload.comment)}
									multiline={true}
									numberOfLines={3}
									style={styles.commentInput}
								/>
							) : (
								Boolean(photo.comment) && (
									<Text style={styles.commentText}>
										{photo.comment}
									</Text>
								)
							)}
						</View>
					))}
				</View>
			) : (
				Boolean(emptyText) && <Text style={styles.emptyText}>{emptyText}</Text>
			)}
			{currentPhotoIndex !== null && (
				<Modal
					visible={true}
					transparent={true}
					onRequestClose={() => setCurrentPhotoIndex(null)}
				>
					<ImageViewer
						close={() => setCurrentPhotoIndex(null)}
						index={currentPhotoIndex}
						imageUrls={imageViewerUrls}
					/>
				</Modal>
			)}
		</View>
	);
};

const createStyles = (theme) => ({
	addButton: {
		alignSelf: 'flex-start',
		marginTop: 4,
		marginBottom: 8,
	},
	photoGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		marginTop: 6,
	},
	photoCard: {
		width: '48%',
		marginRight: '2%',
		marginBottom: 12,
	},
	photoTile: {
		width: '100%',
		height: 116,
		marginBottom: 6,
		borderRadius: 6,
		overflow: 'hidden',
		backgroundColor: theme.mainBg3,
		borderWidth: 1,
		borderColor: theme.border1,
	},
	photo: {
		width: '100%',
		height: '100%',
		resizeMode: 'cover',
	},
	removeButton: {
		position: 'absolute',
		top: 4,
		right: 4,
		height: 24,
		width: 24,
		borderRadius: 12,
		alignItems: 'center',
		justifyContent: 'center',
		backgroundColor: theme.mainBg1,
	},
	removeIcon: {
		color: theme.text1,
	},
	emptyText: {
		color: theme.text2,
		marginTop: 4,
	},
	commentInput: {
		minHeight: 60,
		textAlignVertical: 'top',
	},
	commentText: {
		color: theme.text1,
		marginTop: 2,
	},
});

export default VisualDirectionPhotoGrid;
